import type {
  ChatHistoryMessage,
  CheckinResponse,
  CoachGeneralToggleResponse,
  CoachWorkoutToggleResponse,
  CompleteProjectPayload,
  CreateProjectPayload,
  CreateProjectResult,
  DashboardResponse,
  OptionGroup,
  OptionGroups,
  PersonaCard,
  Project,
  Safety,
  UpdateProjectPayload,
  User,
  UserProfile,
  WorkoutChatHistoryMessage,
  WorkoutCoachResponse,
} from './types';
import { supabase } from './lib/supabase';

// In dev, leave VITE_API_URL unset and the Vite proxy forwards /api/* to the
// local Worker. In prod, set VITE_API_URL to the deployed Worker URL.
const API_BASE = import.meta.env.VITE_API_URL ?? '/api';

// Pull the current access token from Supabase. supabase-js caches in memory and
// auto-refreshes, so this is effectively free on the hot path.
async function authHeader(): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function request<T>(path: string, init?: RequestInit & { auth?: boolean }): Promise<T> {
  const auth = init?.auth !== false;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init?.headers as Record<string, string> | undefined),
    ...(auth ? await authHeader() : {}),
  };
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  if (!res.ok) {
    let message = `${res.status} ${res.statusText}`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      // body wasn't JSON; keep default
    }
    throw new Error(message);
  }
  return (await res.json()) as T;
}

// Public ----------------------------------------------------------------
export async function getOptions(): Promise<OptionGroups> {
  const res = await request<{ groups: OptionGroups }>('/options', { auth: false });
  return res.groups;
}

export async function getOption(field: string): Promise<OptionGroup> {
  return request<OptionGroup>(`/options/${field}`, { auth: false });
}

// Authenticated ---------------------------------------------------------
// All of these implicitly target the current signed-in user — the Worker reads
// user_id from the JWT, never from the request body or query string.
export type ProfilePayload = {
  name?: string;
  unit_preference?: 'metric' | 'imperial';
  primary_goal?: string;
  sex?: 'male' | 'female';
  height_cm?: number | null;
  weight_kg?: number | null;
  target_weight_kg?: number | null;
  self_reported_bf_pct?: number | null;
  activity_level?: string;
  fasting_schedule?: string;
  meal_frequency?: string;
  per_meal_preferences?: Record<string, unknown>;
  dietary_restrictions?: string[];
  health_conditions?: string[];
  beverage_habits?: string;
  equipment_access?: string;
  coach_personality?: string;
  injuries?: string[];
  allergies?: string[];
};

export async function getProfile(): Promise<UserProfile | null> {
  const res = await request<{ profile: UserProfile | null }>('/profile');
  return res.profile;
}

export async function saveProfile(
  payload: ProfilePayload,
): Promise<{ user_id: string; profile: UserProfile }> {
  return request<{ user_id: string; profile: UserProfile }>('/profile', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function getDashboard(): Promise<DashboardResponse> {
  return request<DashboardResponse>('/dashboard');
}

// Projects (P5) ---------------------------------------------------------
export async function getActiveProject(): Promise<{ project: Project | null }> {
  return request<{ project: Project | null }>('/projects/active');
}

// Macro/kcal targets returned from the safety review. Field names mirror the
// projects table columns (and worker safetyReview output) so the result can
// be persisted as-is when the wizard later POSTs /projects.
export type Targets = {
  daily_kcal_target: number;
  protein_target_g: number;
  carb_target_g: number;
  fat_target_g: number;
};

// What POST /projects/preview returns. Always 200 — gate state surfaces as
// safety.level === 'gate' rather than a 422, so the wizard can render the
// warn/gate branch inline before meal prefs are collected. Reuses the
// existing Safety domain type (which already matches the worker shape).
export type ProjectPreview = {
  safety: Safety;
  targets: Targets;
};

// Narrower than CreateProjectPayload — preview only needs the inputs the
// safety review reads (goal + target/timeline + recomp_population). Fasting
// schedule, meal prefs, and safety_override aren't read by the preview
// endpoint; the wizard collects them later and they only matter at create
// time.
export type PreviewProjectPayload = {
  goal: 'maintenance' | 'fat_loss' | 'recomp' | 'muscle_gain';
  target_weight_kg: number | null;
  timeline_weeks: number | null;
  recomp_population: 'beginner' | 'returning' | 'high_bf' | 'override' | null;
};

export async function previewProject(payload: PreviewProjectPayload): Promise<ProjectPreview> {
  return request<ProjectPreview>('/projects/preview', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

// PUT /projects/:id — single-field state mutation, S7 only accepts
// { status: 'cancelled' }. Routes through request() (200 happy path; 404 /
// 409 / 422 surface as Error via request()'s default handling).
export async function cancelProject(id: string): Promise<{ project: Project }> {
  const payload: UpdateProjectPayload = { status: 'cancelled' };
  return request<{ project: Project }>(`/projects/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

// GET /projects/:id — single-project read for the completion screen.
// Returns regardless of status (active / completed / cancelled). 404
// surfaces as Error via request(); the completion screen treats it as a
// hard-error since the route requires a valid project id from the URL.
export async function getProject(id: string): Promise<{ project: Project }> {
  return request<{ project: Project }>(`/projects/${encodeURIComponent(id)}`);
}

// POST /projects/:id/complete — slice D completion-fork write.
// 200 returns { project, user, next_action } with the user row including
// the new maintenance_until / coast_until columns mirrored back so the
// dashboard banner renders without a second getMe call. 409 / 422 / 404
// surface as Error via request().
export async function completeProject(
  id: string,
  next_action: CompleteProjectPayload['next_action'],
): Promise<{ project: Project; user: User | null; next_action: CompleteProjectPayload['next_action'] }> {
  const payload: CompleteProjectPayload = { next_action };
  return request<{ project: Project; user: User | null; next_action: CompleteProjectPayload['next_action'] }>(
    `/projects/${encodeURIComponent(id)}/complete`,
    { method: 'POST', body: JSON.stringify(payload) },
  );
}

// /projects POST is deliberately not routed through request() — it returns
// 201 on success and 422 when the requested pace exceeds the safety gate
// without an explicit override. We surface both as data on the discriminated
// union so the caller can render the gate UI inline; only network / other
// non-2xx errors throw.
export async function createProject(payload: CreateProjectPayload): Promise<CreateProjectResult> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(await authHeader()),
  };
  const res = await fetch(`${API_BASE}/projects`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });
  if (res.status === 201) {
    const body = (await res.json()) as { project: Project; safety: Safety };
    return { saved: true, project: body.project, safety: body.safety };
  }
  if (res.status === 422) {
    const body = (await res.json()) as { error?: string; safety: Safety };
    return { saved: false, safety: body.safety };
  }
  // 409 with stable-token error:'active_project_exists' is treated as data,
  // not error — the caller surfaces body.message in the errorbar and uses
  // body.activeProjectId to route the user to the dashboard's cancel UI via
  // router state. Any other 409 shape (or malformed body) falls through to
  // the generic throw block below; res.clone() leaves the original body
  // available for that fallback path.
  if (res.status === 409) {
    try {
      const body = (await res.clone().json()) as {
        error?: string;
        message?: string;
        activeProjectId?: string;
      };
      if (
        body.error === 'active_project_exists'
        && typeof body.message === 'string'
        && typeof body.activeProjectId === 'string'
      ) {
        return {
          saved: false,
          kind: 'active_project_exists',
          message: body.message,
          activeProjectId: body.activeProjectId,
        };
      }
    } catch {
      /* fall through to generic throw */
    }
  }
  let message = `${res.status} ${res.statusText}`;
  try {
    const body = (await res.json()) as { error?: string };
    if (body.error) message = body.error;
  } catch {
    /* keep default */
  }
  throw new Error(message);
}

export type CheckinPayload = {
  date?: string;
  weight_kg?: number | null;
  steps?: number | null;
  calories?: number | null;
  workout_completed?: boolean;
  mood?: string;
  notes?: string;
  body_fat_pct?: number | null;
  waist_cm?: number | null;
};

export async function postCheckin(payload: CheckinPayload): Promise<CheckinResponse> {
  return request<CheckinResponse>('/checkin', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

// GET /chat/history — repurposed by Brief C as the read-only coach-notes
// feed (context='general' toggle-emission notices). The interactive
// POST /chat write path retired; this read is unchanged.
export async function getChatHistory(): Promise<ChatHistoryMessage[]> {
  const res = await request<{ messages: ChatHistoryMessage[] }>('/chat/history');
  return res.messages;
}

// W.2.a — Workout-coach (POST + history) -------------------------------
// Sentinel returned when the backend rejects with 423 because
// coach_workout_enabled=false. The WorkoutChat screen renders the same
// locked empty state without throwing into the error path.
export type WorkoutChatLockedResponse = { locked: true };

export async function postWorkoutCoachMessage(
  projectId: string,
  message: string,
): Promise<WorkoutCoachResponse | WorkoutChatLockedResponse> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(await authHeader()),
  };
  const res = await fetch(
    `${API_BASE}/projects/${encodeURIComponent(projectId)}/workout-coach`,
    {
      method: 'POST',
      headers,
      body: JSON.stringify({ message }),
    },
  );
  if (res.status === 423) {
    return { locked: true };
  }
  if (!res.ok) {
    let errMessage = `${res.status} ${res.statusText}`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) errMessage = body.error;
    } catch {
      /* keep default */
    }
    throw new Error(errMessage);
  }
  return (await res.json()) as WorkoutCoachResponse;
}

export async function getWorkoutChatHistory(
  projectId: string,
): Promise<WorkoutChatHistoryMessage[]> {
  const res = await request<{ messages: WorkoutChatHistoryMessage[] }>(
    `/projects/${encodeURIComponent(projectId)}/workout-coach/history`,
  );
  return res.messages;
}

// Persona catalog — public endpoint (no auth). Mirrors GET /personas.
export async function getPersonaCatalog(): Promise<PersonaCard[]> {
  const res = await request<{ personas: PersonaCard[] }>('/personas', { auth: false });
  return res.personas;
}

export async function updatePersona(persona: string): Promise<{ persona: string }> {
  return request<{ persona: string }>('/profile/persona', {
    method: 'PATCH',
    body: JSON.stringify({ persona }),
  });
}

export async function updateCoachGeneralEnabled(
  enabled: boolean,
): Promise<CoachGeneralToggleResponse> {
  return request<CoachGeneralToggleResponse>('/profile/coach-general-enabled', {
    method: 'PATCH',
    body: JSON.stringify({ enabled }),
  });
}

export async function updateCoachWorkoutEnabled(
  enabled: boolean,
): Promise<CoachWorkoutToggleResponse> {
  return request<CoachWorkoutToggleResponse>('/profile/coach-workout-enabled', {
    method: 'PATCH',
    body: JSON.stringify({ enabled }),
  });
}

// Account-level (users table) ------------------------------------------
export async function getMe(): Promise<User | null> {
  const res = await request<{ user: User | null }>('/users/me');
  return res.user;
}

export async function saveDateOfBirth(date_of_birth: string): Promise<User> {
  const res = await request<{ user: User }>('/users/me', {
    method: 'PUT',
    body: JSON.stringify({ date_of_birth }),
  });
  return res.user;
}

export type LockProgramPayload = {
  locked_sessions: string[];
  removed_sessions: string[];
};

export type LockProgramResponse = {
  active_program_id: string;
  archived_proposal_id: string;
  edit_loop_sessions: string[];
};

export async function lockWorkoutProgram(
  projectId: string,
  programId: string,
  payload: LockProgramPayload,
): Promise<LockProgramResponse> {
  return request<LockProgramResponse>(
    `/projects/${encodeURIComponent(projectId)}/workout-programs/${encodeURIComponent(programId)}/lock`,
    { method: 'POST', body: JSON.stringify(payload) },
  );
}

export async function deleteAccount(): Promise<{ deleted: boolean; error?: string }> {
  // Custom path — we want the body even on non-2xx so the caller can show the
  // partial-deletion error message verbatim.
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(await authHeader()),
  };
  const res = await fetch(`${API_BASE}/account`, { method: 'DELETE', headers });
  let body: { deleted?: boolean; error?: string } = {};
  try { body = await res.json() as typeof body; } catch { /* keep defaults */ }
  return { deleted: body.deleted === true, ...(body.error ? { error: body.error } : {}) };
}
